import React, { useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Stack,
  Typography,
  Tooltip,
  alpha,
  useTheme,
} from "@mui/material";
import {
  KeyboardArrowDown as ChevronDownIcon,
  KeyboardArrowRight as ChevronRightIcon,
  Dashboard as DashboardIcon,
} from "@mui/icons-material";
import routes from "routes.js";
import { getSecureLocalItem } from "utils/secureSessionStorage";

export function SidebarLinks({ collapsed, onItemClick }) {
  const theme = useTheme();
  const location = useLocation();
  const [expandedGroups, setExpandedGroups] = useState({});
  const [hoveredItem, setHoveredItem] = useState(null);
  const role = getSecureLocalItem("role") || "admin";

  // Get routes safely
  const safeRoutes = routes || [];

  // Filter and group routes
  const groupedRoutes = useMemo(() => {
    try {
      const currentLayout = location.pathname.split("/")[1] || "admin";
      const filtered = safeRoutes.filter(
        (route) =>
          route &&
          route.showInSidebar &&
          route.layout &&
          route.layout.includes(currentLayout) &&
          (route.roles ? route.roles.includes(role) : true),
      );

      return filtered.reduce((groups, route) => {
        if (!route) return groups;
        const group = route.group || "General";
        if (!groups[group]) {
          groups[group] = [];
        }
        groups[group].push(route);
        return groups;
      }, {});
    } catch (error) {
      console.error("Error grouping routes:", error);
      return {};
    }
  }, [location, role, safeRoutes]);

  const toggleGroup = (group) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [group]: !prev[group],
    }));
  };

  const isGroupActive = (groupRoutes) => {
    if (!groupRoutes || !Array.isArray(groupRoutes)) return false;
    return groupRoutes.some(
      (route) =>
        route &&
        route.layout &&
        route.path &&
        location.pathname === `${route.layout}/${route.path}`,
    );
  };

  const isRouteActive = (route) => {
    if (!route || !route.layout || !route.path) return false;
    return location.pathname === `${route.layout}/${route.path}`;
  };

  const isDarkMode = theme.palette.mode === "dark";

  // If no routes found, show empty state
  if (Object.keys(groupedRoutes).length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          p: 3,
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              background: alpha(theme.palette.primary.main, 0.1),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <DashboardIcon
              sx={{ color: theme.palette.primary.main, fontSize: 24 }}
            />
          </Box>
          <Typography
            variant="caption"
            sx={{
              color: isDarkMode ? alpha("#94a3b8", 0.7) : alpha("#6b7280", 0.7),
            }}
          >
            No menu items available
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Stack spacing={0.5}>
      {Object.entries(groupedRoutes).map(([group, groupRoutes], groupIndex) => {
        if (!groupRoutes || !Array.isArray(groupRoutes)) return null;

        const isExpanded = expandedGroups[group] || false;
        const isActiveGroup = isGroupActive(groupRoutes);
        const groupIcon = groupRoutes[0]?.icon || (
          <DashboardIcon sx={{ fontSize: 20 }} />
        );

        return (
          <Box key={group}>
            {/* Group Header - Only show for groups with multiple items */}
            {group !== "Dashboard" && group !== "General" && (
              <motion.div
                whileHover={!collapsed ? { x: 4 } : {}}
                transition={{ duration: 0.2 }}
              >
                <Box
                  onClick={() => toggleGroup(group)}
                  onMouseEnter={() =>
                    !collapsed && setHoveredItem(`group-${groupIndex}`)
                  }
                  onMouseLeave={() => setHoveredItem(null)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: collapsed ? "center" : "space-between",
                    width: "100%",
                    px: collapsed ? 1 : 2,
                    py: 1.5,
                    borderRadius: "12px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    background: isActiveGroup
                      ? alpha(
                          theme.palette.primary.main,
                          isDarkMode ? 0.15 : 0.08,
                        )
                      : "transparent",
                    color: isActiveGroup
                      ? theme.palette.primary.main
                      : isDarkMode
                      ? alpha("#94a3b8", 0.8)
                      : alpha("#4b5563", 0.8),
                    "&:hover": {
                      background: alpha(
                        theme.palette.primary.main,
                        isDarkMode ? 0.1 : 0.05,
                      ),
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  {!collapsed ? (
                    <>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 32,
                            height: 32,
                            flexShrink: 0,
                            borderRadius: "10px",
                            background: isActiveGroup
                              ? alpha(theme.palette.primary.main, 0.15)
                              : "transparent",
                            transition: "all 0.2s",
                          }}
                        >
                          {groupIcon}
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: isActiveGroup ? 600 : 500,
                            fontSize: "0.95rem",
                            letterSpacing: "0.3px",
                            lineHeight: 1.5,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          {group}
                        </Typography>
                      </Stack>
                      <motion.div
                        animate={{ rotate: isExpanded ? 180 : 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                      >
                        <ChevronDownIcon sx={{ fontSize: 18, opacity: 0.6 }} />
                      </motion.div>
                    </>
                  ) : (
                    <Tooltip title={group} placement="right" arrow>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 36,
                          height: 36,
                          borderRadius: "10px",
                          background: isActiveGroup
                            ? alpha(theme.palette.primary.main, 0.15)
                            : "transparent",
                          transition: "all 0.2s",
                        }}
                      >
                        {groupIcon}
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              </motion.div>
            )}

            {/* Group Links */}
            <AnimatePresence initial={false}>
              {(isExpanded || group === "Dashboard" || group === "General") && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25, ease: "easeInOut" }}
                  style={{ overflow: "hidden" }}
                >
                  <Stack
                    spacing={0.5}
                    sx={{
                      pl: group !== "Dashboard" && group !== "General" ? 3 : 0,
                      mt: 0.5,
                    }}
                  >
                    {groupRoutes.map((route, idx) => {
                      if (!route) return null;

                      const isActive = isRouteActive(route);
                      const itemId = `route-${group}-${idx}`;

                      return (
                        <motion.div
                          key={idx}
                          initial={{ x: -10, opacity: 0 }}
                          animate={{ x: 0, opacity: 1 }}
                          transition={{ delay: idx * 0.03, duration: 0.2 }}
                          whileHover={!collapsed ? { x: 4 } : {}}
                        >
                          <Box
                            component={Link}
                            to={`${route.layout}/${route.path}`}
                            onClick={onItemClick}
                            onMouseEnter={() =>
                              !collapsed && setHoveredItem(itemId)
                            }
                            onMouseLeave={() => setHoveredItem(null)}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: collapsed
                                ? "center"
                                : "flex-start",
                              gap: collapsed ? 0 : 1.5,
                              width: "100%",
                              px: collapsed ? 1 : 1.5,
                              py: 1,
                              borderRadius: "10px",
                              textDecoration: "none",
                              position: "relative",
                              transition: "all 0.2s ease",
                              background: isActive
                                ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                                : "transparent",
                              color: isActive
                                ? "#ffffff"
                                : isDarkMode
                                ? alpha("#cbd5e1", 0.7)
                                : alpha("#4b5563", 0.8),
                              "&:hover": {
                                background: isActive
                                  ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
                                  : alpha(
                                      theme.palette.primary.main,
                                      isDarkMode ? 0.1 : 0.05,
                                    ),
                                color: isActive
                                  ? "#ffffff"
                                  : theme.palette.primary.main,
                              },
                            }}
                          >
                            {/* Active indicator bar */}
                            {isActive && !collapsed && (
                              <motion.div
                                layoutId="active-indicator"
                                transition={{
                                  type: "spring",
                                  stiffness: 500,
                                  damping: 30,
                                }}
                                style={{
                                  position: "absolute",
                                  left: 0,
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  width: 3,
                                  height: 20,
                                  borderRadius: "0 3px 3px 0",
                                  background: theme.palette.primary.main,
                                }}
                              />
                            )}

                            {/* Icon container */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 32,
                                height: 32,
                                borderRadius: "8px",
                                transition: "all 0.2s",
                                ml: collapsed ? 0 : 0,
                              }}
                            >
                              {route.icon || (
                                <DashboardIcon sx={{ fontSize: 20 }} />
                              )}
                            </Box>

                            {/* Label */}
                            {!collapsed && (
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: isActive ? 600 : 400,
                                  fontSize: "0.95rem",
                                  flex: 1,
                                }}
                              >
                                {route.name || "Unnamed"}
                              </Typography>
                            )}

                            {/* Active chevron */}
                            {!collapsed && isActive && (
                              <motion.div
                                initial={{ x: -5, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ duration: 0.2 }}
                              >
                                <ChevronRightIcon
                                  sx={{ fontSize: 16, opacity: 0.8 }}
                                />
                              </motion.div>
                            )}
                          </Box>

                          {/* Tooltip for collapsed state */}
                          {collapsed && hoveredItem === itemId && (
                            <Tooltip
                              title={route.name || "Menu Item"}
                              placement="right"
                              arrow
                              open={true}
                              sx={{
                                "& .MuiTooltip-tooltip": {
                                  backgroundColor: isDarkMode
                                    ? "#1e293b"
                                    : "#1f2937",
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                  padding: "6px 12px",
                                  borderRadius: "8px",
                                },
                              }}
                            />
                          )}
                        </motion.div>
                      );
                    })}
                  </Stack>
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
        );
      })}
    </Stack>
  );
}
